import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import classNames from "classnames";
import * as api from "../../Service/api";
import { CustomerSearchField } from "../../Components/CustomerSearchField";
import "../../Styles/components/tags.scss";

const MIN_SEARCH_TEXT_LENGTH = 2;

const toPlain = (data) => {
  if (data == null) return data;
  if (typeof data.asMutable === "function") return data.asMutable({ deep: true });
  if (typeof data.toJS === "function") return data.toJS();
  return data;
};

const normalizeSummaryResults = (data) => {
  const plain = toPlain(data);
  if (!plain) return [];
  if (Array.isArray(plain)) return plain;
  if (Array.isArray(plain.content)) return plain.content;
  if (Array.isArray(plain.summaries)) return plain.summaries;
  if (Array.isArray(plain.businessCustomers)) return plain.businessCustomers;
  if (Array.isArray(plain.data)) return plain.data;
  if (Array.isArray(plain.items)) return plain.items;
  return [];
};

const getBusinessCustomerId = (summary) =>
  summary?.id ?? summary?.businessCustomer?.id ?? null;

const normalizeTags = (tags) => {
  const plain = toPlain(tags);
  if (!plain) return [];
  if (Array.isArray(plain)) {
    return plain
      .map((tag) => {
        if (typeof tag === "string") return tag;
        return tag?.name ?? tag?.label ?? null;
      })
      .filter(Boolean);
  }
  if (typeof plain === "string") {
    return plain
      .replace(/[{}]/g, "")
      .split(/[,|/]/)
      .map((tag) => tag.replace(/"/g, "").trim())
      .filter(Boolean);
  }
  return [];
};

const SegmentTagsRow = ({ segment, tags }) => {
  const tagList = normalizeTags(tags);
  if (!segment && tagList.length === 0) return null;

  return (
    <div className="link-customer-modal__segment-tags mt-2">
      {segment && (
        <div className="segment-tag-div mb-2">
          <span className="segment-class">{segment}</span>
        </div>
      )}
      {tagList.length > 0 && (
        <div className="tags-container">
          {tagList.map((tag) => (
            <div key={tag} className="tag-div-main">
              <span className="tag-class">{tag}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const mapSearchResult = (summary) => {
  const bc = summary?.businessCustomer || {};
  const segmentName = bc.segment?.name ?? null;
  return {
    bcId: getBusinessCustomerId(summary),
    name: bc.name ?? null,
    phoneNo: bc.phoneNoMasked ?? bc.phoneNo ?? null,
    customerNum: bc.customerNum ?? null,
    reference: bc.reference ?? null,
    segment: segmentName && segmentName !== null ? segmentName : null,
    tags: normalizeTags(summary?.tags ?? bc.tags),
  };
};

function LinkBusinessCustomerModal({
  visible,
  onHide,
  businessId,
  sourceCustomer,
  onLinkPrimary,
  linking,
  linkError,
}) {
  const { t } = useTranslation("common");
  const [searchText, setSearchText] = useState("");
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedPrimaryId, setSelectedPrimaryId] = useState(null);
  const [selfLinkError, setSelfLinkError] = useState(false);
  const searchRequestRef = useRef(0);

  const sourceBcId = sourceCustomer?.bcId ?? null;

  const resetModalState = useCallback(() => {
    searchRequestRef.current += 1;
    setSearchText("");
    setSearching(false);
    setHasSearched(false);
    setResults([]);
    setSelectedPrimaryId(null);
    setSelfLinkError(false);
  }, []);

  useEffect(() => {
    if (!visible) {
      resetModalState();
    }
  }, [visible, resetModalState]);

  const runSearch = async () => {
    const text = searchText.trim();
    if (!visible || !businessId || text.length < MIN_SEARCH_TEXT_LENGTH) return;

    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    setSearching(true);
    setHasSearched(false);
    setSelectedPrimaryId(null);
    setSelfLinkError(false);

    const query = `?searchText=${encodeURIComponent(text.toLowerCase())}`;
    const response = await api.getBusinessCustomerSummarySearch(businessId, query);
    if (requestId !== searchRequestRef.current) return;
    if (response.ok) {
      setResults(normalizeSummaryResults(response.data));
    } else {
      setResults([]);
    }
    setSearching(false);
    setHasSearched(true);
  };

  const selectableResults = useMemo(() => {
    return results
      .map(mapSearchResult)
      .filter((row) => row.bcId != null && String(row.bcId) !== String(sourceBcId));
  }, [results, sourceBcId]);

  const handleSelectResult = (primaryBcId) => {
    if (String(primaryBcId) === String(sourceBcId)) {
      setSelfLinkError(true);
      setSelectedPrimaryId(null);
      return;
    }
    setSelfLinkError(false);
    setSelectedPrimaryId(primaryBcId);
  };

  const handleLinkClick = () => {
    if (!selectedPrimaryId) return;
    if (String(selectedPrimaryId) === String(sourceBcId)) {
      setSelfLinkError(true);
      return;
    }
    setSelfLinkError(false);
    onLinkPrimary(selectedPrimaryId);
  };

  const showSearchPrompt =
    searchText.trim().length > 0 && searchText.trim().length < MIN_SEARCH_TEXT_LENGTH;
  const showNoResults =
    hasSearched &&
    !searching &&
    selectableResults.length === 0;

  const sourceDisplayName =
    sourceCustomer?.name && sourceCustomer.name !== null
      ? sourceCustomer.name
      : sourceCustomer?.bcId;

  const canLink = Boolean(selectedPrimaryId) && !linking;

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={t("common.cancel")}
        className="p-button-outlined"
        onClick={onHide}
        disabled={linking}
      />
      <Button
        label={
          linking
            ? t("business_customers.linking_customer")
            : t("business_customers.link_customer")
        }
        className="link-customer-modal__link-btn"
        onClick={handleLinkClick}
        disabled={!canLink}
        loading={linking}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={t("business_customers.link_customer_title")}
      style={{ width: "560px", maxWidth: "95vw" }}
      contentStyle={{ padding: "1.5rem 1.75rem" }}
      footer={footer}
      modal
      dismissableMask={!linking}
      closable={!linking}
      className="link-customer-modal"
    >
      {sourceCustomer && (
        <div className="link-customer-modal__source-banner mb-3">
          <i className="pi pi-link link-customer-modal__source-icon" />
          <div>
            <div className="link-customer-modal__source-label">
              {t("business_customers.link_customer_source")}
            </div>
            <div className="link-customer-modal__source-name">{sourceDisplayName}</div>
            {sourceCustomer.phoneNo && sourceCustomer.phoneNo !== null && (
              <div className="link-customer-modal__source-meta">
                {t("common.number")}: {sourceCustomer.phoneNo}
              </div>
            )}
            <SegmentTagsRow
              segment={sourceCustomer.segment}
              tags={sourceCustomer.tags}
            />
          </div>
        </div>
      )}

      <div className="field mb-0">
        <label htmlFor="link-customer-search" className="block mb-1 link-customer-modal__section-title">
          {t("business_customers.link_customer_search_label")}
        </label>
        <CustomerSearchField
          id="link-customer-search"
          value={searchText}
          disabled={linking}
          placeholder={t("business_customers.link_customer_search_placeholder")}
          onChange={(value) => {
            searchRequestRef.current += 1;
            setSelfLinkError(false);
            setSelectedPrimaryId(null);
            setSearchText(value);
            setResults([]);
            setHasSearched(false);
            setSearching(false);
          }}
          onSearch={runSearch}
          onClear={() => {
            searchRequestRef.current += 1;
            setSelfLinkError(false);
            setSelectedPrimaryId(null);
            setSearchText("");
            setResults([]);
            setHasSearched(false);
            setSearching(false);
          }}
        />
      </div>

      {showSearchPrompt && (
        <p className="link-customer-modal__hint mt-2 mb-0">
          {t("business_customers.link_customer_search_placeholder")}
        </p>
      )}

      {searching && (
        <div className="link-customer-modal__loading mt-3">
          <ProgressSpinner style={{ width: "28px", height: "28px" }} strokeWidth="4" />
        </div>
      )}

      {showNoResults && (
        <div className="link-customer-modal__empty mt-3">{t("common.no_data")}</div>
      )}

      {!searching && selectableResults.length > 0 && (
        <div className="link-customer-modal__results mt-3">
          {selectableResults.map((row) => {
            const isSelected = String(row.bcId) === String(selectedPrimaryId);
            return (
              <button
                key={row.bcId}
                type="button"
                disabled={linking}
                onClick={() => handleSelectResult(row.bcId)}
                className={classNames("link-customer-modal__result", {
                  "link-customer-modal__result--selected": isSelected,
                })}
              >
                <div className="link-customer-modal__result-status">
                  <span className="customer-link-badge customer-link-badge--primary">
                    {t("business_customers.primary")}
                  </span>
                </div>
                <div className="link-customer-modal__result-name">{row.name}</div>
                <div className="link-customer-modal__result-meta">
                  {row.phoneNo}
                  {row.customerNum ? ` · ${row.customerNum}` : ""}
                  {row.reference && row.reference !== null ? ` · ${row.reference}` : ""}
                </div>
                <SegmentTagsRow segment={row.segment} tags={row.tags} />
              </button>
            );
          })}
        </div>
      )}

      {selfLinkError && (
        <Message
          severity="error"
          text={t("business_customers.link_customer_self_error")}
          className="w-full mt-3"
        />
      )}

      {linkError && (
        <Message
          severity="error"
          text={t("business_customers.link_customer_error")}
          className="w-full mt-3"
        />
      )}
    </Dialog>
  );
}

export default LinkBusinessCustomerModal;
